import React, { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { images } from '../../components/shared/assets';
import { initializeAllFeatures } from '../../utils/interactiveFeatures';
import UnifiedNavigation from '../../components/Navigation/UnifiedNavigation';
import InstagramSection from '../../components/BrandHomePage/InstagramSection';
import Footer from '../../components/Footer/Footer';
import { GlacierVideoExploration } from '../../components/BrandHomePage/MeaningfulExplorationSection/GlacierVideoExploration/GlacierVideoExploration';
import { ContentfulText, ContentfulMultilineText } from '../../components/Contentful';
import { usePageContent, useSiteSettings } from '../../hooks/useContentful';
import '../OurStory/OurStoryPage.css';

const AboutAtacama: React.FC = () => {
  const carouselRef = useRef<HTMLDivElement>(null);
  
  // Fetch content from Contentful
  const { data: pageContentData } = usePageContent('our-story');
  const { data: siteSettingsData } = useSiteSettings();
  const pageContent = pageContentData?.[0]?.fields as any;
  const siteSettings = siteSettingsData?.[0]?.fields as any;
  
  const scrollCarousel = () => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({ left: 650, behavior: 'smooth' });
    }
  };

  // Initialize all interactive features
  useEffect(() => {
    const cleanup = initializeAllFeatures();
    return cleanup;
  }, []);

  return (
    <div className="our-story-page">
      {/* Navigation - Atacama variant with destination */}
      <UnifiedNavigation variant="light" destination="atacama" />

      {/* Content Sections with Scroll Reveals - Same structure as homepage */}
      <div className="content-wrapper">
        
        {/* Timeline Carousel Section - Horizontal scrolling like Figma design */}
        <div data-scroll-section="timeline" className="diptych-carousel scroll-reveal-section">
          <div className="timeline-carousel-wrapper">
            
            {/* Opening Diptych - Text + Image */}
            <div className="timeline-diptych">
              <div className="timeline-text-block">
                <div className="timeline-intro-text">
                  <p>
                    The small town of San Pedro de Atacama stretches back 3,000 years, when it started out as a trading ground for llama caravans. These Andean traders made their way through Bolivia and Northern Argentina to the Pacific Coast, transporting the best llamas for breeding and wool; feathers from the Amazon jungle; and coca leaves and herbs from the Altiplano.
                  </p>
                </div>
              </div>
              <div className="timeline-intro-image">
                <img src="/assets/atacama-factsheet-hero.jpg" alt="Awasi Atacama landscape" />
              </div>
            </div>

            {/* Timeline Carousel Container */}
            <div className="timeline-carousel-container">
              
              {/* 2007 - Calendar Text Block */}
              <div className="timeline-calendar-block">
                <div className="timeline-year-number">2007</div>
                <div className="timeline-content">
                  <h3>How it's started</h3>
                  <p>
                    Awasi is located in the town of San Pedro de Atacama, a short walk from the main square with its market and historic church. This proximity allows our guests to experience authentic "town life", while enjoying privacy within the hotel. Since opening in 2007, we have continued to offer memorable tailor-made experiences, sharing with our guests the culture, history and landscape of the Atacama Desert.
                  </p>
                </div>
              </div>

              {/* Video Section */}
              <div className="timeline-video-section">
                <div 
                  className="timeline-video"
                  style={{ 
                    backgroundImage: `linear-gradient(90deg, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0.3) 100%), url('/assets/fullviewportatacama.jpg')`
                  }}
                />
              </div>

              {/* Image Text Blurb Section */}
              <div className="timeline-image-text-section">
                <div className="timeline-section-image">
                  <img src={images.destinationsPage.properties.atacama} alt="Awasi Atacama story" />
                </div>
                <div className="timeline-section-text">
                  <p>
                    It is an ancient site surrounded by an ornate adobe wall that historically sheltered shepherds and herders. Using local materials and techniques, we built the lodge with adobe, stone and wood.
                  </p>
                </div>
                <div className="timeline-section-body">
                  <p>
                    Awasi Atacama is a place for travellers to enjoy the warmth of staying at a close friend's house as they create memories in the Atacama Desert.
                  </p>
                  <p>
                  </p>
                </div>
              </div>

              {/* 2016 - Calendar Text Block */}
              <div className="timeline-calendar-block">
                <div className="timeline-year-number">2016</div>
                <div className="timeline-content">
                  <h3>Privacy and relaxation</h3>
                  <p>
                    The hotel has 12 rooms built from adobe, stone and wood. Each room has a large bathroom, private patio and is designed with privacy and relaxation in mind. Our restaurant, for the exclusive use of Awasi guests, offers a daily menu using the finest local ingredients, adapted to your requirements.
                  </p>
                </div>
              </div>

              {/* Collage Section */}
              <div className="timeline-collage-section">
                <div className="timeline-collage">
                  <img 
                    src="/assets/images/video1.png" 
                    alt="Atacama experiences" 
                    className="collage-image collage-image-1"
                  />
                  <img 
                    src="/assets/images/video2.png" 
                    alt="Desert landscapes" 
                    className="collage-image collage-image-2"
                  />
                  <img 
                    src="/assets/images/video3.png" 
                    alt="Cultural encounters" 
                    className="collage-image collage-image-3"
                  />
                </div>
              </div>

              {/* Large Image */}
              <div className="timeline-large-image">
                <img src="/assets/experiencepagebreak.jpg" alt="Awasi Atacama landscape" />
              </div>

              {/* 2025 - Calendar Text Block */}
              <div className="timeline-calendar-block">
                <div className="timeline-year-number">2025</div>
                <div className="timeline-content">
                  <h3>Endless possibilities</h3>
                  <p>
                    From pleasant walks to more demanding treks, horseback riding, bicycle riding, bird watching, astronomic tours, visits to archaeological sites, salt flats and geysers, or to incredible valleys hidden in the midst of the desert, the experiences during your stay at Awasi are endless.
                  </p>
                </div>
              </div>

            </div>
            
            {/* Carousel Navigation Arrow - Bug #58 */}
            <button 
              className="timeline-carousel-arrow"
              onClick={scrollCarousel}
              aria-label="Scroll timeline"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Meaningful Exploration Carousel Section */}
        <div data-scroll-section="exploration" className="meaningful-exploration-wrapper scroll-reveal-section">
          <GlacierVideoExploration />
        </div>

        {/* Full Viewport Waterfall Video Section */}
        <div data-scroll-section="waterfall-video" className="waterfall-video-section scroll-reveal-section">
          <div 
            className="waterfall-video-background"
            style={{ backgroundImage: `url('/assets/images/gallery-atacama-desert-view.png')` }}
          >
            {/* Optional mute button overlay */}
            <button className="video-mute-button">
              <div className="mute-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M11 5L6 9H2V15H6L11 19V5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M23 9L17 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M17 9L23 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </button>
          </div>
        </div>

        {/* Team & Talents Section - +300 people, +17 nationalities */}
        <div data-scroll-section="team-talents" className="team-talents-section scroll-reveal-section">
          <div className="team-talents-header">
            <h1>Uncountable talents</h1>
            <div className="team-talents-description">
              <p>
                Our staff create first impressions, accompany our guests on their adventures and strive to exceed expectations — sending them home with unforgettable memories. Our team is made of passionate people who are proud ambassadors of these lands. Each of them is a key component of the Awasi Lodges.
              </p>
              <button className="join-team-btn">
                <span>JOIN OUR TEAM</span>
              </button>
            </div>
          </div>

          {/* Team Member Cards */}
          <div className="team-members-grid">
            <div className="team-member-card">
              <img src="/assets/images/519a5fa1880e5bbcb22afb5ce8981a4528823628.png" alt="Juan García" />
              <div className="team-member-info">
                <h3>Sebastián Prieto</h3>
                <span className="team-role">AWASI ATACAMA - HEAD CHEF</span>
                <p>The remoteness encourages the use of native desert herbs, alongside Andean potatoes cultivated in highland valleys. Our cuisine brings authentic flavours and a deep connection to the unique ecosystem of the Atacama Desert.</p>
                <Link to="/our-story#team" className="awasi-link awasi-link--read-more">
                  <span className="awasi-link-text">READ MORE</span>
                  <div className="awasi-link-underline">
                    <svg viewBox="0 0 76 2" fill="none">
                      <path d="M0 1L76 1" />
                    </svg>
                  </div>
                </Link>
              </div>
            </div>
            <div className="team-member-card">
              <img src="/assets/images/858f0defa8422b7f106e566acde779f3d9a78726.png" alt="Maria Condori" />
              <div className="team-member-info">
                <h3>Juan Ignacio García Machicao</h3>
                <span className="team-role">AWASI ATACAMA - EXCURSIONS MANAGER</span>
                <p>Our guides help monitor the local wildlife populations and keep hunters away. They share with our guests the culture, history and landscape of the Atacama Desert.</p>
                <Link to="/our-story#team" className="awasi-link awasi-link--read-more">
                  <span className="awasi-link-text">READ MORE</span>
                  <div className="awasi-link-underline">
                    <svg viewBox="0 0 76 2" fill="none">
                      <path d="M0 1L76 1" />
                    </svg>
                  </div>
                </Link>
              </div>
            </div>
            <div className="team-member-card">
              <img src="/assets/images/0900d5204eb6e5804f3a3f031befd03ac9cff02f.png" alt="Pedro Mamani" />
              <div className="team-member-info">
                <h3>Paula Bertotto</h3>
                <span className="team-role">AWASI ATACAMA - GUIDE</span>
                <p>From pleasant walks to more demanding treks, our guides ensure each experience is tailored to your preferences, revealing the desert's hidden treasures and ancient wisdom.</p>
                <Link to="/our-story#team" className="awasi-link awasi-link--read-more">
                  <span className="awasi-link-text">READ MORE</span>
                  <div className="awasi-link-underline">
                    <svg viewBox="0 0 76 2" fill="none">
                      <path d="M0 1L76 1" />
                    </svg>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Equality, Diversity & Inclusion Section - Using CarbonNeutral Pattern */}
        <div data-scroll-section="equality" className="equality-diversity-section scroll-reveal-section">
          <div className="equality-diversity-container">
            <div className="equality-diversity-headline">
              <h2>Equality, Diversity & Inclusion at Awasi</h2>
            </div>
            <img
              src="/assets/images/destinationsAtacama.png"
              alt="Awasi Atacama Conservation"
              className="equality-diversity-image"
            />
            <div className="equality-diversity-content-box">
              <div className="equality-diversity-content-box-bg" />
              <div className="equality-diversity-content">
                <div className="equality-diversity-text-group">
                  <h3 className="equality-diversity-title">
                    Internship at Awasi Atacama
                  </h3>
                  <p className="equality-diversity-description">
                    We work together with the local community, offering internships and first work experience for students and hiring those who fit the role once they finish their studies. Interns at Awasi Atacama are trained in several different areas of the hotel for a couple of months.
                  </p>
                </div>
                <button className="equality-diversity-learn-more">
                  <span>READ MORE</span>
                </button>
              </div>
              <div className="equality-diversity-page-toggles">
                <button className="equality-diversity-page-toggle equality-diversity-page-toggle--active">
                  <span>1</span>
                </button>
                <button className="equality-diversity-page-toggle">
                  <span>2</span>
                </button>
                <button className="equality-diversity-page-toggle">
                  <span>3</span>
                </button>
                <button className="equality-diversity-page-toggle">
                  <span>4</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Press & Awards Section - Figma Exact Design */}
        <div data-scroll-section="awards" className="press-awards-section scroll-reveal-section">
          <div className="press-awards-container">
            <h2 className="press-awards-title">Press & Awards</h2>
            
            <div className="press-awards-list">
              <div className="press-award-item">
                <div className="award-text">Travel + Leisure The Top 100 Hotels in the World 2020</div>
                <div className="award-properties">AWASI ATACAMA</div>
              </div>
              
              <div className="press-award-item">
                <div className="award-text">Conde Nast Traveller Gold List 2019</div>
                <div className="award-properties">AWASI ATACAMA</div>
              </div>
              
              <div className="press-award-item">
                <div className="award-text">The Telegraph The world's 50 greatest hotels.</div>
                <div className="award-properties">AWASI ATACAMA</div>
              </div>
              
              <div className="press-award-item">
                <div className="award-text">Relais & Châteaux Member</div>
                <div className="award-properties">AWASI ATACAMA</div>
              </div>
            </div>
            
            <button className="see-more-btn">
              <span>SEE MORE</span>
            </button>
          </div>
        </div>


      </div>

      {/* Footer */}
      <InstagramSection />
      <Footer variant="atacama" />
    </div>
  );
};

export default AboutAtacama;